/* Oshea PDP — compact layout + native sticky cart.
 *
 * Relocates the theme's own title / rating / price blocks above the gallery on
 * mobile (so the Judge.me rating stays live rather than a stale metafield),
 * renders benefit chips, and drives a sticky bar that hands off to the two apps
 * already on the page:
 *   Add to cart → POST /cart/add.js → window.ytCartShell.open()   (YourToken)
 *   Buy now     → POST /cart/add.js → window.triggerGokwikCustomCheckout()
 */
(() => {
  const dataEl = document.querySelector('[data-opd-data]');
  if (!dataEl) return;

  let data = {};
  try {
    data = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  const DESKTOP = 1024;
  const product = document.querySelector('.product');
  const blocks = document.querySelector('.product__info-container .product__blocks');

  /* ---------------- helpers ---------------- */
  const moved = []; // {node, parent, next} so the layout can be put back

  const relocate = (node, target) => {
    if (!node || !target) return;
    moved.push({ node, parent: node.parentNode, next: node.nextSibling });
    target.appendChild(node);
  };

  const restoreAll = () => {
    while (moved.length) {
      const m = moved.pop();
      if (m.parent) m.parent.insertBefore(m.node, m.next);
    }
  };

  const svgTick =
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" ' +
    'stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M5 12l5 5L20 7"/></svg>';

  /* ---------------- 1. head block above the gallery ---------------- */
  let head = null;
  let benefitsEl = null;

  const buildHead = () => {
    if (head || !product || !blocks) return;

    const titleBlock = blocks.querySelector('.product__block h1')?.closest('.product__block');
    const priceBlock = blocks.querySelector('.product__block--price');
    const ratingBlock = blocks
      .querySelector('.jdgm-prev-badge, .jdgm-widget')
      ?.closest('.product__block');

    if (!titleBlock && !priceBlock) return;

    head = document.createElement('div');
    head.className = 'opd-head';

    relocate(titleBlock, head);

    if (data.tagline) {
      const p = document.createElement('p');
      p.className = 'opd-tagline';
      p.textContent = data.tagline;
      head.appendChild(p);
    }

    const row = document.createElement('div');
    row.className = 'opd-head__row';
    relocate(priceBlock, row);

    if (ratingBlock) {
      const rWrap = document.createElement('div');
      rWrap.className = 'opd-head__rating';
      row.appendChild(rWrap);
      relocate(ratingBlock, rWrap);
    }

    head.appendChild(row);
    product.insertBefore(head, product.firstChild);

    // "Save 24%" reads as a deal cue on the image, not as a line of text in the
    // info column — move it onto the gallery, top-left.
    const badges = blocks.querySelector('.product__block--badges');
    const viewer = product.querySelector('.product__media-gallery-viewer-wrap');
    if (badges && viewer) relocate(badges, viewer);
  };

  /* ---------------- 2. benefit chips under the gallery ---------------- */
  // The theme's own tick-mark list, as plain strings. Used as the benefit source
  // for the ~15% of products whose woo.short_description has no "✅" lines, so
  // every product gets a Key benefits block rather than some getting chips and
  // the rest getting the old tick list.
  const tickLines = () =>
    Array.from(blocks?.querySelectorAll('.wtp-tag1') || [])
      .map((t) => {
        const spans = t.querySelectorAll('span');
        const raw = (spans[spans.length - 1]?.textContent || '').replace(/\s+/g, ' ').trim();
        // Some of these lines are written as "Label: one sentence of detail".
        // A chip is a glance, not a paragraph, so keep the label when there is
        // one short enough to stand on its own.
        const head = raw.split(/[:–—]/)[0].trim();
        return head && head.length <= 34 && head.length < raw.length ? head : raw;
      })
      .filter(Boolean);

  const buildBenefits = () => {
    if (benefitsEl || !product) return;
    let list = Array.isArray(data.benefits) ? data.benefits.filter(Boolean) : [];
    if (!list.length) list = tickLines();
    if (!list.length) return;

    benefitsEl = document.createElement('div');
    benefitsEl.className = 'opd-benefits';

    const label = document.createElement('p');
    label.className = 'opd-benefits__label';
    label.textContent = 'Key benefits';
    benefitsEl.appendChild(label);

    const ul = document.createElement('ul');
    ul.className = 'opd-chips';
    list.slice(0, 6).forEach((text) => {
      const li = document.createElement('li');
      li.className = 'opd-chip';
      li.innerHTML = svgTick + '<span></span>';
      li.querySelector('span').textContent = text;
      ul.appendChild(li);
    });
    benefitsEl.appendChild(ul);

    const media = product.querySelector('.product__media-wrapper');
    if (media && media.nextSibling) product.insertBefore(benefitsEl, media.nextSibling);
    else product.appendChild(benefitsEl);

    // The theme prints its own tick-mark list of the same selling points further
    // down the info column. Hide it so the page does not say the same thing
    // twice.
    //
    // That list is one custom_liquid block, always built from .wtp-section1 /
    // .wtp-tag1 markup, so match on the markup rather than on the wording: the
    // two lists are curated separately and often phrase the same benefit
    // differently ("Removes Dark Spots & Blemishes" vs "Removes Acne Scars,
    // Dark Spots & Blemishes"), which no text comparison can be trusted to
    // catch.
    const TICKS = '.wtp-section1, .wtp-tags1, .wtp-tag1';
    blocks?.querySelectorAll('.product__block--custom_liquid').forEach((b) => {
      if (b === head || b.closest('.opd-head')) return;
      if (b.querySelector(TICKS)) b.dataset.opdDup = 'true';
    });
  };

  /* ---------------- 2b. BUY MORE, SAVE MORE label repair ----------------
   * The YourToken tier titles read "Get 2ﬁ Off" — the percent sign in the
   * app's config is literally U+FB01, the "fi" ligature, which is what a "%"
   * turns into when copy-pasted out of a PDF or Word. It is wrong in the
   * YourToken dashboard, not here, so fix it there too; this only stops
   * customers seeing it in the meantime. Scoped to a digit followed by the
   * ligature so real "fi" ligatures in words are left alone, and it is a
   * no-op once the source is corrected.
   */
  const LIGATURE = /(\d)ﬁ/g;

  // "Pack of 2" -> "Buy 2", matching the first tier's own "Buy 1" wording. It
  // also earns back height: at a 110px card width "Pack of 2, Get 2% Off" wraps
  // to two lines where "Buy 2, Get 2% Off" fits on one, taking each card from
  // 65px to 51px. Worth changing in the YourToken dashboard too.
  const PACK_OF = /\bPack of (\d+)/gi;

  const fixTierLabels = () => {
    document
      .querySelectorAll(
        '.yt-volume-vertical-details-title, .yt-volume-vertical-details-subtitle, .yt-bundle-header-title, .yt-bundle-header-subtitle'
      )
      .forEach((el) => {
        const t = el.textContent;
        const fixed = t.replace(LIGATURE, '$1%').replace(PACK_OF, 'Buy $1');
        if (fixed !== t) el.textContent = fixed;
        LIGATURE.lastIndex = 0;
        PACK_OF.lastIndex = 0;
      });
  };

  // The app block renders after the page does, so watch for it rather than
  // guessing a delay. Replacing the text re-triggers the observer once, then
  // there is nothing left to match and it settles.
  const watchBundle = () => {
    if (!blocks) return;
    fixTierLabels();
    new MutationObserver(fixTierLabels).observe(blocks, { childList: true, subtree: true });
  };

  /* ---------------- 2c. content tabs move down the page ----------------
   * "Who's It Good For?" through "Product Overview" sit in the buy column,
   * between the cart offers and the footer trust row, pushing the real buying
   * decision down. They belong with the rest of the reading material, so they
   * are moved to sit directly above the FAQ section.
   *
   * Only the mobile copies move: the theme renders each collapsible twice,
   * once plain (desktop) and once with `md:hidden`, and only the md:hidden set
   * lives inside .product__info-container. Desktop is therefore untouched.
   */
  let tabsEl = null;

  const moveContentTabs = () => {
    if (tabsEl) return;
    const faq = document.querySelector('[id*="custom_liquid_PTQK8Q"]');
    const tabs = Array.from(
      document.querySelectorAll('.product__info-container .product__block--collapsible_tab')
    );
    if (!faq || !faq.parentNode || !tabs.length) return;

    tabsEl = document.createElement('div');
    tabsEl.className = 'opd-tabs';
    faq.parentNode.insertBefore(tabsEl, faq);
    tabs.forEach((t) => relocate(t, tabsEl));
  };

  /* ---------------- 2d. OFFERS below BUY MORE, SAVE MORE ----------------
   * The theme template puts the cart-offers app block before the volume
   * bundle. The bundle is the decision ("how many do I buy?"); the offers are
   * the reason it pays off, so they read better underneath it. Both are
   * sibling .product__block--@app elements, so this is a straight DOM move,
   * pushed through relocate() so revertMobile puts it back on desktop.
   */
  let offersMoved = false;

  const reorderOffers = () => {
    if (offersMoved || !blocks) return;
    const offers = blocks
      .querySelector('[data-yt-product-block="products-offers"]')
      ?.closest('.product__block');
    const bundle = blocks
      .querySelector('[data-yt-product-block="volume-bundle"]')
      ?.closest('.product__block');
    if (!offers || !bundle || offers === bundle || !bundle.parentNode) return;

    // only act while offers still comes first, so this is idempotent
    const offersIsBefore =
      offers.compareDocumentPosition(bundle) & Node.DOCUMENT_POSITION_FOLLOWING;
    if (!offersIsBefore) return;

    moved.push({ node: offers, parent: offers.parentNode, next: offers.nextSibling });
    bundle.parentNode.insertBefore(offers, bundle.nextSibling);
    offersMoved = true;
  };

  /* ---------------- 2e. reviews below the FAQs ----------------
   * "Customers rate this store" (a Judge.me store-summary product block) and
   * the Judge.me review widget with its pagination sat between the buy column
   * and the related products, ~1.7k px of scroll before the customer reached
   * anything else. Both move to the end of the page: after the FAQs, before
   * the footer.
   *
   * The review widget is a sibling section of the FAQ section so it moves as
   * it is; the store summary is a product block and would lose the page gutter
   * at main level, so it gets an .opd-reviews wrapper.
   */
  let reviewsEl = null;

  const moveReviews = () => {
    if (reviewsEl) return;
    const faq = document.querySelector('[id*="custom_liquid_PTQK8Q"]');
    if (!faq || !faq.parentNode) return;

    const summaryBlock = document
      .querySelector('.jdgm-store-summary-widget')
      ?.closest('.product__block');
    const reviewsSec = document.querySelector('.jdgm-review-widget')?.closest('.shopify-section');
    if (!summaryBlock && !reviewsSec) return;

    reviewsEl = document.createElement('div');
    reviewsEl.className = 'opd-reviews';
    faq.parentNode.insertBefore(reviewsEl, faq.nextSibling);

    if (summaryBlock) relocate(summaryBlock, reviewsEl);
    if (reviewsSec) {
      moved.push({ node: reviewsSec, parent: reviewsSec.parentNode, next: reviewsSec.nextSibling });
      reviewsEl.parentNode.insertBefore(reviewsSec, reviewsEl.nextSibling);
    }
  };

  /* ---------------- 2f. round 4 relocations + label fixes ---------------- */

  // The "Order within the next …" delivery countdown is rendered INSIDE the
  // theme's buy-buttons block, which is now display:none — so hiding that
  // block took the countdown with it. Move it out (below the cart offers,
  // where it belongs) rather than trying to un-hide a child of a hidden node.
  let countdownMoved = false;

  const moveCountdown = () => {
    if (countdownMoved || !blocks) return;
    // The app renders THREE .samita-countdown nodes: the delivery message, a
    // style-only one, and an empty placeholder (#samita-countdown-false).
    // Pick the one that actually holds the message, or the empty placeholder
    // gets moved and the latch closes on nothing.
    const cd = document
      .querySelector('.samita_countdown_delivery_container')
      ?.closest('.samita-countdown');
    const offers = blocks
      .querySelector('[data-yt-product-block="products-offers"]')
      ?.closest('.product__block');
    if (!cd || !offers || !offers.parentNode) return;
    moved.push({ node: cd, parent: cd.parentNode, next: cd.nextSibling });
    offers.parentNode.insertBefore(cd, offers.nextSibling);
    countdownMoved = true;
  };

  // "Selling Fast! / 1000+ sold / Stock / Last 30 days" docks above the sticky
  // cart instead of sitting in the body. The app's own element moves; the CSS
  // flattens its three stacked rows into one strip.
  let urgencyMoved = false;

  // Set once the sticky bar is wired up, below. Docking the strip changes the
  // bar's height, and the page has to reserve the new one.
  let syncBarHeight = () => {};

  const moveUrgency = () => {
    if (urgencyMoved || !sticky) return;
    const up = document.querySelector('.urgency-progress');
    if (!up || up.closest('.opd-sticky')) return;
    // The strip earns its place with the urgency line ("Selling Fast!", "Only 7
    // left!", "Out of Stock") and the sold count. The block prints both only
    // when custom.last_30_days_sales >= 30, or when stock is low — so on a
    // steadily-selling, well-stocked product every line is empty except
    // "Stock: 48", and a full-width strip above the sticky cart carrying one
    // stock number reads as a rendering fault rather than as information.
    // Leave those products without one.
    const hasUrgency =
      (up.querySelector('.urgency-text')?.textContent || '').trim() ||
      (up.querySelector('.sold-text')?.textContent || '').trim();
    if (!hasUrgency) {
      if (!('opdHidUrgency' in up.dataset)) {
        up.dataset.opdHidUrgency = up.style.display;
        up.style.display = 'none';
      }
      return;
    }
    moved.push({ node: up, parent: up.parentNode, next: up.nextSibling });
    sticky.insertBefore(up, sticky.firstChild);
    urgencyMoved = true;
    syncBarHeight();
  };

  // The selected value is hidden by CSS; strip the colon it leaves behind so
  // the label reads "Size" rather than "Size:".
  const tidyVariantLabel = () => {
    document
      .querySelectorAll('.product__block--variant_picker .form__label .font-body-bolder')
      .forEach((el) => {
        const t = el.textContent.replace(/\s*:\s*$/, '');
        if (t !== el.textContent) el.textContent = t;
      });
  };

  // Put the option name ("Size") on the same row as the swatches.
  //
  // It cannot be done in CSS: the name lives in the fieldset's <legend>, and a
  // rendered legend is not a flex item — the browser lays it out above the
  // fieldset's content box whatever you set. So the label div is lifted out of
  // the legend into the flex flow and the now-empty legend is hidden. The
  // theme also sets width:100% on .form__label, which flex-basis:auto then
  // inherits, so the CSS has to force width:auto or it still takes a full row.
  let variantLabelsFlattened = false;

  const flattenVariantLabels = () => {
    if (variantLabelsFlattened) return;
    const sets = document.querySelectorAll('.product__block--variant_picker fieldset');
    if (!sets.length) return;
    sets.forEach((fs) => {
      const legend = fs.querySelector('legend');
      const label = legend?.querySelector('.form__label');
      if (!legend || !label) return;
      moved.push({ node: label, parent: label.parentNode, next: label.nextSibling });
      fs.insertBefore(label, legend.nextSibling);
      legend.dataset.opdHidden = '1';
    });
    variantLabelsFlattened = true;
  };

  // The collapsible headings ("Who's It Good For?" … "Product Overview") are a
  // <p class="h5"> inside each <summary>, and the theme writes
  // style="font-size:18px !important" on them from a block setting. An inline
  // !important outranks every stylesheet rule, so the only way to bring them
  // onto the page's type scale is to remove that one declaration; the rule in
  // oshea-pdp.css then applies. The original is kept so desktop gets it back.
  const shrinkTabHeadings = () => {
    document
      .querySelectorAll('.opd-tabs summary [style*="font-size"], [id*="custom_liquid_PTQK8Q"] summary [style*="font-size"]')
      .forEach((el) => {
        if (el.dataset.opdFs) return;
        el.dataset.opdFs = el.style.getPropertyValue('font-size');
        el.dataset.opdFsPri = el.style.getPropertyPriority('font-size');
        el.style.removeProperty('font-size');
      });
  };

  const restoreTabHeadings = () => {
    document.querySelectorAll('[data-opd-fs]').forEach((el) => {
      if (el.dataset.opdFs) el.style.setProperty('font-size', el.dataset.opdFs, el.dataset.opdFsPri || '');
      delete el.dataset.opdFs;
      delete el.dataset.opdFsPri;
    });
  };

  /* ---------------- "How to use <kit>" — facial-kit step carousel ----------------
   * Facial-kit products carry a custom_liquid block (.wfk-container) built from
   * the custom.wfk_* metafields: one .wfk-row per step, each holding a product
   * shot, a "Step N" how-to, and the constituent product's name, description
   * and benefit list. Stacked, five steps measured 4,465px of scroll in
   * 32px/20px type that matched nothing else on the page.
   *
   * The rows become a tab strip — Step 1 … Step N, one panel at a time — and
   * the CSS brings the type onto the page's scale: 4,465px -> ~470px.
   *
   * Every row stays in the DOM, only hidden, so the content is still there for
   * search engines; with JS off the customer simply sees them all, as before.
   * No-ops on every product that is not a kit.
   */
  let wfkTabs = null;

  const buildWfk = () => {
    if (wfkTabs) return;
    const box = document.querySelector('.wfk-container');
    if (!box) return;
    // The block renders one row per IMAGE, and some kits carry more images than
    // they have documented steps — Fruitmix has nine images against five steps,
    // leaving four picture-only shells with no how-to, product name, or
    // benefits. Stacked they were easy to miss; as tabs, "Step 7" opening onto
    // a lone photo is worse than no tab at all. Skip them.
    //
    // The shells are not missing those elements — the block still prints a
    // "Step 7" heading and an EMPTY <p> — so the test has to be on text, not on
    // the presence of a node: does the row say anything at all beyond its
    // number?
    const rows = Array.from(box.querySelectorAll('.wfk-row')).filter(
      (r) =>
        (r.querySelector('.wfk-step p')?.textContent || '').trim() ||
        (r.querySelector('.wfk-list')?.textContent || '').trim()
    );
    if (!rows.length) return;

    // The one-panel-at-a-time rules in oshea-pdp-wfk.css hang off this class, so
    // a customer with JS off (or if this function bails above) still sees every
    // row stacked, exactly as the theme shipped it.
    box.classList.add('opd-wfk-on');

    const h2 = box.querySelector('h2');
    if (h2 && !h2.dataset.opdFull) {
      // The heading is the whole SEO title — "Why Papaya Facial Kit – Deep
      // Cleansing, Instant Glow & Brightening Facial at Home" — five lines on a
      // phone. Keep the part before the dash; the rest is the page title anyway.
      //
      // Then "Why <kit>" becomes "How to use <kit>", which is what the section
      // actually is now that it reads as numbered steps. The prefix is only
      // swapped when it is there, so a kit whose heading is worded differently
      // is left alone rather than mangled.
      const short = h2.textContent.split(/[–—]/)[0].trim();
      const retitled = short.replace(/^why\s+/i, 'How to use ');
      if (retitled && retitled !== h2.textContent.trim()) {
        h2.dataset.opdFull = h2.textContent;
        h2.textContent = retitled;
      }
    }

    const tabs = document.createElement('div');
    tabs.className = 'opd-wfk-tabs';
    tabs.setAttribute('role', 'tablist');
    tabs.setAttribute('aria-label', 'Kit steps');

    rows.forEach((row, i) => {
      const label = row.querySelector('.wfk-step h4')?.textContent.trim() || `Step ${i + 1}`;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'opd-wfk-tab';
      btn.textContent = label;
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.addEventListener('click', () => {
        rows.forEach((r) => delete r.dataset.opdActive);
        tabs.querySelectorAll('.opd-wfk-tab').forEach((t) => t.setAttribute('aria-selected', 'false'));
        row.dataset.opdActive = '1';
        btn.setAttribute('aria-selected', 'true');
      });
      tabs.appendChild(btn);
      if (i === 0) row.dataset.opdActive = '1';
    });

    (h2 || box.firstElementChild)?.insertAdjacentElement('afterend', tabs);
    wfkTabs = tabs;
  };

  const revertWfk = () => {
    wfkTabs?.remove();
    wfkTabs = null;
    document.querySelector('.wfk-container')?.classList.remove('opd-wfk-on');
    document.querySelectorAll('.wfk-row[data-opd-active]').forEach((r) => delete r.dataset.opdActive);
    const h2 = document.querySelector('.wfk-container h2[data-opd-full]');
    if (h2) {
      h2.textContent = h2.dataset.opdFull;
      delete h2.dataset.opdFull;
    }
  };

  // "Pair it with" — the theme's own complementary-products block.
  //
  // Round 5 built this by cloning the related-products section through the
  // Section Rendering API with intent=complementary. That was a mistake once
  // pairings were actually curated: the theme ALREADY ships a
  // .product__block--complementary block (heading "Perfect Match With", with a
  // real slider), which renders itself the moment complementary products exist
  // — so the clone became a second "You may also like" sitting above the real
  // one. The clone is gone; this moves the theme's own block.
  //
  // It is relocated out of the buy column to the end of the content run, after
  // Certifications, so the product's own story finishes before the page starts
  // selling other things.
  let pairsMoved = false;
  const PAIRS_TITLE = 'Pair it with';

  const renamePairs = () => {
    const h = document.querySelector('.complementary-products__heading');
    if (h && h.textContent.trim() !== PAIRS_TITLE) h.textContent = PAIRS_TITLE;
  };

  const movePairs = () => {
    renamePairs();
    if (pairsMoved) return;
    const block = document.querySelector('.product__block--complementary');
    const content = document.querySelector('[data-opd-content]');
    // Wait until .opd-content has been placed, or the block would be parked in
    // a container that is still hidden next to the sticky bar.
    if (!block || !content || !content.dataset.opdPlaced) return;
    moved.push({ node: block, parent: block.parentNode, next: block.nextSibling });
    // after Certifications — i.e. last, but still above the optional social
    // proof line, which is a footer for the whole run rather than a section.
    const social = content.querySelector('.opd-social');
    if (social) content.insertBefore(block, social);
    else content.appendChild(block);
    pairsMoved = true;
  };

  // Theme section heading. Also changeable in the theme editor — this keeps it
  // right without touching templates/product.json.
  const renameRelated = () => {
    const h = document.querySelector('[id*="related_products"] .section__heading');
    if (h && h.textContent.trim() !== 'You May also Like') h.textContent = 'You May also Like';
  };

  /* Both renames have to be WATCHED, not just re-applied on a timer.
   *
   * The related-products heading sits in a <motion-element> the theme reveals
   * on scroll, and the complementary block's slider re-renders its container
   * when it initialises — both of which overwrite the text. The timed latePass
   * stops at 10s, so a customer who scrolls down to these sections after that
   * (which is most of them: they are near the foot of a long page) sees the
   * theme's original wording come back. That is exactly what happened.
   *
   * An observer per section fixes it for good. Writing the text re-triggers the
   * observer once, then the !== guard makes the second call a no-op and it
   * settles — the same pattern watchBundle already uses.
   */
  let relObserved = false;
  let compObserved = false;

  const watchRenames = () => {
    if (!relObserved) {
      const rel = document.querySelector('[id*="related_products"]');
      if (rel) {
        new MutationObserver(renameRelated).observe(rel, {
          childList: true,
          subtree: true,
          characterData: true,
        });
        relObserved = true;
      }
    }
    if (!compObserved) {
      // Observes the block itself, so it keeps working after movePairs()
      // relocates it — an observer follows the node, not its position.
      const comp = document.querySelector('.product__block--complementary');
      if (comp) {
        new MutationObserver(renamePairs).observe(comp, {
          childList: true,
          subtree: true,
          characterData: true,
        });
        compObserved = true;
      }
    }
  };

  // Helps with / Hero ingredients / Real results / Certifications / social
  // proof. Rendered server-side by the section (so the metafield content is in
  // the HTML, not injected by script) and parked next to the sticky bar until
  // there is somewhere to put it: directly above "You may also like", so the
  // product's own story finishes before the page starts selling other things.
  // Falls back to the collapsible tabs, then the FAQs, if that section is
  // absent on a given template.
  const placeContent = () => {
    const content = document.querySelector('[data-opd-content]');
    if (!content) return;
    // Same re-assert as placeContentDesk(), and for the same reason: the mobile
    // branch of sync() now begins with revertDesktop(), which hides the block,
    // and sync() runs again as late app blocks settle. A bare early return here
    // left the sections placed but invisible from the second pass onwards.
    if (content.dataset.opdPlaced) {
      content.hidden = false;
      return;
    }
    const anchorEl =
      document.querySelector('[id*="related_products"]') ||
      tabsEl ||
      document.querySelector('[id*="custom_liquid_PTQK8Q"]');
    if (!anchorEl || !anchorEl.parentNode) return;
    relocate(content, anchorEl.parentNode);
    anchorEl.parentNode.insertBefore(content, anchorEl);
    content.hidden = false;
    content.dataset.opdPlaced = '1';
  };

  /* ---------------- desktop ----------------
   *
   * Everything above this point is the mobile rebuild. Desktop kept the theme's
   * own two-column layout and, until now, showed none of the metafield content:
   * .opd-content was rendered into the HTML and then switched off wholesale by
   * a `@media (min-width: 1024px) { .opd-content { display: none } }` rule. So a
   * desktop customer saw no concern pills, no skin types, no hero ingredients
   * and no certifications, and no tagline under the title.
   *
   * The split below is deliberate and was chosen with the merchant:
   *   - the two pill sections (what it helps with, who it suits) go INTO the buy
   *     column, directly above the size picker and price, because they answer
   *     "is this for me?" at the moment of deciding;
   *   - hero ingredients, the results claim and certifications stay as a
   *     full-width band at the same anchor mobile uses — above "You may also
   *     like" — because they need the width and they are the closing argument.
   *
   * The mobile path owns `moved` / restoreAll(); this keeps its own stack so the
   * two can never unwind each other's DOM moves when the viewport crosses 1024.
   */
  const movedDesk = [];

  const relocateDesk = (node, target, before) => {
    if (!node || !target) return;
    movedDesk.push({ node, parent: node.parentNode, next: node.nextSibling });
    target.insertBefore(node, before || null);
  };

  const restoreDesk = () => {
    while (movedDesk.length) {
      const m = movedDesk.pop();
      if (m.parent) m.parent.insertBefore(m.node, m.next);
    }
  };

  let deskTagline = null;
  let deskPills = null;

  // The tagline is a metafield the catalogue fills for every active product. On
  // mobile buildHead() prints it inside the relocated head block; desktop keeps
  // the theme's own title where it is, so it just gets a sibling paragraph.
  const buildDeskTagline = () => {
    if (deskTagline || !blocks || !data.tagline) return;
    const titleBlock = blocks.querySelector('.product__block h1')?.closest('.product__block');
    if (!titleBlock) return;
    const p = document.createElement('p');
    p.className = 'opd-tagline opd-tagline--desk';
    p.textContent = data.tagline;
    titleBlock.insertAdjacentElement('afterend', p);
    deskTagline = p;
  };

  const placeContentDesk = () => {
    const content = document.querySelector('[data-opd-content]');
    if (!content) return;
    // Already placed: only re-assert visibility and stop. This is not belt and
    // braces — sync() runs several times as late-rendering app blocks settle,
    // and its desktop branch starts with revertMobile(), which ends by setting
    // content.hidden = true. Without this the band was placed correctly on the
    // first pass and then hidden again 1.2s later, for good.
    if (content.dataset.opdPlacedDesk) {
      content.hidden = false;
      return;
    }
    const anchorEl =
      document.querySelector('[id*="related_products"]') ||
      document.querySelector('[id*="custom_liquid_PTQK8Q"]');
    if (!anchorEl || !anchorEl.parentNode) return;

    relocateDesk(content, anchorEl.parentNode, anchorEl);
    content.hidden = false;
    content.classList.add('opd-content--desk');
    content.dataset.opdPlacedDesk = '1';

    // Lift the two pill sections into the buy column. Matched on data-opd-sec,
    // set by sections/oshea-pdp.liquid, rather than on heading text — the
    // "Suitable For" heading is Skin Type or Hair Type depending on the product
    // and the concerns heading changes wording on combos.
    if (!blocks) return;
    const concerns = content.querySelector('[data-opd-sec="concerns"]');
    const skin = content.querySelector('[data-opd-sec="skin"]');
    if (!concerns && !skin) return;

    // Above the size picker, so price and Add to cart stay adjacent. Falls back
    // to the price block on products with a single variant and no picker.
    const anchorBlock =
      blocks.querySelector('.product__block--variant_picker') ||
      blocks.querySelector('.product__block--price');
    if (!anchorBlock) return;

    deskPills = document.createElement('div');
    deskPills.className = 'product__block opd-deskpills';
    blocks.insertBefore(deskPills, anchorBlock);
    if (concerns) relocateDesk(concerns, deskPills);
    if (skin) relocateDesk(skin, deskPills);
  };

  // Desktop keeps the stacked rows — on a wide screen every step is visible at
  // once, which beats a tab strip — but the heading should read the same on both
  // devices. Same rewrite buildWfk() does, without the tabs; revertWfk() puts
  // the original text back from data-opd-full.
  const renameWfkHeading = () => {
    const h2 = document.querySelector('.wfk-container h2');
    if (!h2 || h2.dataset.opdFull) return;
    const short = h2.textContent.split(/[–—]/)[0].trim();
    const retitled = short.replace(/^why\s+/i, 'How to use ');
    if (retitled && retitled !== h2.textContent.trim()) {
      h2.dataset.opdFull = h2.textContent;
      h2.textContent = retitled;
    }
  };

  // Desktop tail order. As the template ships, the page runs:
  //   … "Why <kit>" · "Inspired by Nature" icon band · reviews · You may also
  //   like · FAQs
  // so the FAQs — the last thing a hesitating customer reads — sit below the
  // reviews and below a shelf of other products. This lifts the FAQs' two
  // neighbours out and drops them back in after the FAQs, giving:
  //   … · You may also like · FAQs · "Inspired by Nature" · reviews
  //
  // Only two sections actually move: the third one in that original run,
  // video_hero_hyxpwd, is disabled in the template and renders nothing.
  //
  // The reviews section is found through .jdgm-review-widget rather than by its
  // section id, which is an opaque hash — the same handle moveReviews() uses on
  // mobile. Judge.me injects the widget well after this script first runs, so
  // the two halves carry separate flags and latePass() keeps calling until both
  // land; the band never waits on the reviews to appear.
  let deskBandMoved = false;
  let deskReviewsMoved = false;

  const moveTailDesk = () => {
    if (deskBandMoved && deskReviewsMoved) return;
    const faq = document.querySelector('[id*="custom_liquid_PTQK8Q"]');
    if (!faq || !faq.parentNode) return;

    if (!deskBandMoved) {
      const band = document.querySelector('[id*="buttons_with_icon"]');
      if (band && band !== faq) {
        relocateDesk(band, faq.parentNode, faq.nextSibling);
        deskBandMoved = true;
      }
    }

    if (!deskReviewsMoved) {
      const reviews = document.querySelector('.jdgm-review-widget')?.closest('.shopify-section');
      // Sit after the band when it has already moved, so the pair keeps the
      // order it had before — band first, then reviews.
      const after = (deskBandMoved && document.querySelector('[id*="buttons_with_icon"]')) || faq;
      if (reviews && reviews !== after && after.parentNode) {
        relocateDesk(reviews, after.parentNode, after.nextSibling);
        deskReviewsMoved = true;
      }
    }
  };

  const applyDesktop = () => {
    buildDeskTagline();
    placeContentDesk();
    renameWfkHeading();
    moveTailDesk();
  };

  const revertDesktop = () => {
    restoreDesk();
    deskBandMoved = false;
    deskReviewsMoved = false;
    deskTagline?.remove();
    deskTagline = null;
    deskPills?.remove();
    deskPills = null;
    const content = document.querySelector('[data-opd-content]');
    if (content) {
      content.hidden = true;
      content.classList.remove('opd-content--desk');
      delete content.dataset.opdPlacedDesk;
    }
    revertWfk();
  };

  // The theme already ships Product Overview open; this only guarantees it,
  // so the requirement does not silently depend on a theme setting.
  const openProductOverview = () => {
    document.querySelectorAll('.product__block--collapsible_tab details').forEach((d) => {
      const label = d.querySelector('summary')?.textContent || '';
      if (/product overview/i.test(label) && !d.open) d.open = true;
    });
  };

  const applyMobile = () => {
    buildHead();
    buildBenefits();
    reorderOffers();
    moveCountdown();
    moveUrgency();
    moveContentTabs();
    placeContent();
    moveReviews();
    openProductOverview();
    tidyVariantLabel();
    flattenVariantLabels();
    renameRelated();
    shrinkTabHeadings();
    movePairs();
    watchRenames();
    buildWfk();
    syncBarHeight();
    document.querySelectorAll('[data-opd-dup]').forEach((n) => (n.style.display = 'none'));
  };

  const revertMobile = () => {
    restoreAll();
    head?.remove();
    head = null;
    benefitsEl?.remove();
    benefitsEl = null;
    tabsEl?.remove();
    tabsEl = null;
    reviewsEl?.remove();
    reviewsEl = null;
    restoreTabHeadings();
    revertWfk();
    const up = document.querySelector('.urgency-progress');
    if (up && 'opdHidUrgency' in up.dataset) {
      up.style.display = up.dataset.opdHidUrgency;
      delete up.dataset.opdHidUrgency;
    }
    pairsMoved = false;
    offersMoved = false;
    countdownMoved = false;
    urgencyMoved = false;
    variantLabelsFlattened = false;
    document
      .querySelectorAll('.product__block--variant_picker legend[data-opd-hidden]')
      .forEach((l) => delete l.dataset.opdHidden);
    const content = document.querySelector('[data-opd-content]');
    if (content) {
      content.hidden = true;
      delete content.dataset.opdPlaced;
    }
    document.querySelectorAll('[data-opd-dup]').forEach((n) => (n.style.display = ''));
  };

  /* ---------------- 3. sticky bar ---------------- */
  const sticky = document.querySelector('[data-opd-sticky]');

  const form = () =>
    document.querySelector('form[action*="/cart/add"]:not([data-opd-ignore])');

  const variantId = () => {
    const f = form();
    const input = f?.querySelector('[name="id"]');
    return input ? input.value : null;
  };

  const quantity = () => {
    const f = form();
    const q = f?.querySelector('[name="quantity"]');
    const n = q ? parseInt(q.value, 10) : 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  /* ---- BUY MORE, SAVE MORE handoff ----
   * Picking "Buy 3" in the YourToken bundle does NOT change the product
   * form: quantity stays 1. There is also no active quantity-based automatic
   * discount in Shopify, so the 2% / 5% is applied by the app's own add-to-cart
   * path, not by anything we could reproduce with /cart/add.js. So whenever a
   * multi-unit tier is selected, hand the click to the app's own button and let
   * it do whatever it does; our bar only adds directly for the single-unit case.
   */
  const activeTier = () => document.querySelector('.yt-volume-vertical-tier-item-active');

  const tierQty = () => {
    const n = parseInt(activeTier()?.dataset.minimumQuantity, 10);
    return Number.isFinite(n) && n > 1 ? n : 0;
  };

  const tierPriceText = () =>
    activeTier()?.querySelector('.yt-volume-vertical-details-price-discount')?.textContent.trim() || null;

  const bundleButton = () => document.querySelector('.yt-volume-add-to-cart-button');

  /* ---- pack MRP ----
   * A multi-unit tier DOES have a "was" price: the single-unit MRP times the
   * pack quantity. Round 5 hid it on the reasoning that a pack has no compare-at
   * price of its own, which threw away the strongest number on the bar — three
   * of a ₹495 product is ₹1,071.60 against ₹1,485 of MRP.
   *
   * The unit MRP is read out of the theme's OWN struck-out price rather than a
   * number stored at render time, so it follows variant changes for free. It is
   * multiplied, then re-grouped for the store's locale: en-IN, because Shopify
   * renders this shop's money with Indian digit grouping (₹1,485, ₹1,07,160) and
   * a browser-locale default would disagree with the server-rendered prices
   * elsewhere on the page.
   *
   * The currency symbol and its spacing are copied from whichever string the bar
   * is quoting as the live price, so the two lines never punctuate differently
   * ("₹1,071.6" over "MRP ₹1,485", not "₹1,071.6" over "MRP ₹ 1,485"): the theme
   * writes "₹ 495" and the bundle app writes "₹376".
   */
  const MONEY = /\d[\d,]*(?:\.\d+)?/;

  const packMrp = (unitText, qty, styleFrom) => {
    const m = MONEY.exec(unitText || '');
    if (!m) return null;
    const unit = parseFloat(m[0].replace(/,/g, ''));
    if (!Number.isFinite(unit) || !Number.isFinite(qty) || qty < 2) return null;
    const decimals = (m[0].split('.')[1] || '').length;
    const total = (unit * qty).toLocaleString('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    const prefix = /^\s*([^\d\s]+\s*)/.exec(styleFrom || unitText);
    return prefix ? prefix[1] + total : unitText.replace(m[0], total);
  };

  const root = () => (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  // Used before handing off to GoKwik, so checkout never opens on a cart that
  // has not caught up with the click.
  const waitForCart = async (want, ms = 6000) => {
    const t0 = Date.now();
    while (Date.now() - t0 < ms) {
      try {
        const res = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        if (cart && cart.item_count >= want) return true;
      } catch (e) {}
      await new Promise((r) => setTimeout(r, 300));
    }
    return false;
  };

  const addToCart = async () => {
    const id = variantId();
    if (!id) throw new Error('no variant');
    const res = await fetch(`${(window.Shopify && Shopify.routes && Shopify.routes.root) || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: quantity() }] }),
    });
    if (!res.ok) throw new Error(`cart/add ${res.status}`);
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    return res.json();
  };

  const busy = (btn, on, label) => {
    btn.classList.toggle('is-busy', on);
    if (on) {
      btn.dataset.opdLabel = btn.textContent;
      btn.textContent = label;
    } else if (btn.dataset.opdLabel) {
      btn.textContent = btn.dataset.opdLabel;
    }
  };

  const track = (event, params) => {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event }, params));
    } catch (e) {}
  };

  if (sticky) {
    const atc = sticky.querySelector('[data-opd-atc]');
    const buy = sticky.querySelector('[data-opd-buy]');

    atc?.addEventListener('click', async () => {
      busy(atc, true, 'Adding…');
      try {
        const qty = tierQty();
        const viaBundle = qty > 1 && bundleButton();
        if (viaBundle) {
          // The app adds the tier and opens its own drawer.
          bundleButton().click();
          track('add_to_cart', { source: 'sticky_bar', bundle_qty: qty });
        } else {
          await addToCart();
          track('add_to_cart', { source: 'sticky_bar' });
          if (window.ytCartShell && typeof window.ytCartShell.open === 'function') {
            window.ytCartShell.open();
          } else {
            window.location.href = `${root()}cart`;
          }
        }
      } catch (e) {
        console.error('[oshea-pdp]', e);
        form()?.requestSubmit?.();
      } finally {
        busy(atc, false);
      }
    });

    buy?.addEventListener('click', async () => {
      busy(buy, true, 'Please wait…');
      try {
        const qty = tierQty();
        if (qty > 1 && bundleButton()) {
          bundleButton().click();
          await waitForCart(qty);
          track('begin_checkout', { source: 'sticky_bar', bundle_qty: qty });
        } else {
          await addToCart();
          track('begin_checkout', { source: 'sticky_bar' });
        }
        if (typeof window.triggerGokwikCustomCheckout === 'function') {
          window.triggerGokwikCustomCheckout();
        } else {
          window.location.href = '/checkout';
        }
      } catch (e) {
        console.error('[oshea-pdp]', e);
        window.location.href = '/checkout';
      } finally {
        busy(buy, false);
      }
    });

    // Visible from the moment the page loads, not only after the in-page buy
    // buttons scroll away — the bar is the primary path to cart on mobile, so
    // it should never be a thing the customer has to scroll to discover.
    const showBar = () => {
      if (window.innerWidth >= DESKTOP) {
        sticky.classList.remove('is-on');
        document.body.style.removeProperty('padding-bottom');
        return;
      }
      sticky.hidden = false;
      sticky.classList.add('is-on');
      // The bar measures ~67px on its own and ~110px once the urgency strip
      // docks on top of it. The stylesheet has to reserve one figure for every
      // product, and reserving the taller one leaves 43px of blank page under
      // the footer wherever the strip is absent. Reserve what the bar actually
      // measures instead.
      document.body.style.setProperty('padding-bottom', sticky.offsetHeight + 8 + 'px', 'important');
    };
    window.addEventListener('resize', showBar);
    // moveUrgency() and the passes below call this explicitly, which is what
    // actually guarantees the reservation is right; the observer is only there
    // to catch a later reflow (a webfont landing, a price string wrapping).
    syncBarHeight = showBar;
    if ('ResizeObserver' in window) new ResizeObserver(showBar).observe(sticky);
    showBar();

    // Mirror the theme's price block into the bar so variant changes follow.
    const priceSource = document.querySelector('.product__block--price');
    const nowEl = sticky.querySelector('[data-opd-price]');
    const mrpEl = sticky.querySelector('[data-opd-mrp]');
    if (priceSource && nowEl) {
      const sync = () => {
        // A selected multi-unit bundle tier wins: the bar must quote what the
        // button is about to add, not the single-unit price. The tier's own
        // rendered text is used so there is no money-formatting to get wrong.
        const qty = tierQty();
        const tierText = qty > 1 ? tierPriceText() : null;
        if (tierText) {
          nowEl.textContent = tierText;
          if (mrpEl) {
            // MRP x pack quantity, struck through. Hidden only when the variant
            // has no compare-at price at all, which is the same rule the
            // single-unit branch below follows.
            const packed = packMrp(priceSource.querySelector('s')?.textContent, qty, tierText);
            if (packed) {
              mrpEl.textContent = packed;
              mrpEl.hidden = false;
            } else {
              mrpEl.hidden = true;
            }
          }
          return;
        }

        // This theme (FoxEcom) prefixes its price classes with "f-" and puts the
        // compare-at price in a bare <s>, so the usual Dawn selectors match
        // nothing. Take the first visible price item that is not the struck-out
        // one, and accept either naming.
        const items = Array.from(priceSource.querySelectorAll('.f-price-item, .price-item'));
        const nowEls = items.filter(
          (e) => !e.closest('s') && e.offsetParent !== null && e.textContent.trim()
        );
        const now = (nowEls[0] || items.find((e) => !e.closest('s')))?.textContent.trim();
        const cmp = priceSource.querySelector('s');

        if (now) nowEl.textContent = now;
        if (mrpEl) {
          const c = cmp?.textContent.trim();
          if (c && c !== now) {
            mrpEl.textContent = c;
            mrpEl.hidden = false;
          } else {
            mrpEl.hidden = true;
          }
        }
      };
      new MutationObserver(sync).observe(priceSource, { childList: true, subtree: true, characterData: true });
      // the bundle lives outside the price block, so watch it too
      if (blocks) new MutationObserver(sync).observe(blocks, { attributes: true, attributeFilter: ['class'], subtree: true });
      sync();
    }
  }

  /* ---------------- run ---------------- */
  const mq = window.matchMedia(`(max-width: ${DESKTOP - 0.02}px)`);

  /* Tear the other layout down before building this one. Order matters on the
   * desktop branch: revertMobile() ends with revertWfk(), which restores the
   * "Why <kit>" heading, so applyDesktop() has to rename it afterwards — the
   * other way round the rename would be undone in the same tick. */
  const sync = () => {
    if (mq.matches) {
      revertDesktop();
      applyMobile();
    } else {
      revertMobile();
      applyDesktop();
    }
  };

  /* Some of this can only be done after third-party scripts have run:
   * - the Countdown Sami app injects "Order within the next …" well after
   *   DOMContentLoaded, so a single early pass finds nothing to move;
   * - the complementary-products block renders from its own script, so there is
   *   nothing to move or observe until it does.
   * The two renames no longer depend on this schedule — watchRenames() puts a
   * MutationObserver on each section, which outlives it. Every one of these is
   * guarded and idempotent, so re-running is free. */
  const latePass = () => {
    // Desktop needs the same schedule: the related-products section and the
    // "Why <kit>" block both render after this script first runs, so a single
    // early pass finds no anchor and no heading. applyDesktop() is guarded and
    // idempotent, so the repeat calls cost nothing.
    if (!mq.matches) {
      applyDesktop();
      return;
    }
    moveCountdown();
    moveUrgency();
    renameRelated();
    fixTierLabels();
    shrinkTabHeadings();
    movePairs();
    watchRenames();
    buildWfk();
    syncBarHeight();
  };

  const start = () => {
    sync();
    watchBundle();
    // Judge.me and other app blocks render late; re-check once they settle.
    setTimeout(sync, 1200);
    [600, 1500, 3000, 6000, 10000].forEach((ms) => setTimeout(latePass, ms));
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  mq.addEventListener ? mq.addEventListener('change', sync) : mq.addListener(sync);
})();